import { forwardRef, RefObject, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import {
  audioLoadedThunk,
  audioLoadingThunk,
  playbackEndedThunk,
  playbackFailedThunk,
  playbackPausedThunk,
  playbackSeekedThunk,
  playbackSeekingThunk,
  playbackStartedThunk,
  updateCurrentTimeThunk,
  updateMediaDurationThunk,
} from 'store/modules/player';
import ReduxState from 'store/ReduxState';

import { CurrentlyPlaying, handleCurrentlyPlaying } from './hls-helper';

const buildEventHandlers = (current, dispatch) => ({
  canplay: () => dispatch(audioLoadedThunk()),
  ended: () => dispatch(playbackEndedThunk()),
  error: () =>
    dispatch(playbackFailedThunk(current.error.code, current.error.message)),
  loadstart: () => dispatch(audioLoadingThunk()),
  loadedmetadata: ({ target }) =>
    dispatch(updateMediaDurationThunk(target.duration)),
  pause: () => dispatch(playbackPausedThunk()),
  play: () => dispatch(playbackStartedThunk()),
  seeked: () => dispatch(playbackSeekedThunk()),
  seeking: () => dispatch(playbackSeekingThunk()),
  timeupdate: (event) => dispatch(updateCurrentTimeThunk(event.target.currentTime)),
});

const AudioSourceSetup = forwardRef(
  (_props, { current }: RefObject<HTMLAudioElement>) => {
    const currentlyPlaying = useSelector<
      ReduxState,
      CurrentlyPlaying | undefined
    >(({ player }) => player.currentlyPlaying);
    const dispatch = useDispatch();

    useEffect(() => {
      if (current) {
        handleCurrentlyPlaying({
          current,
          currentlyPlaying: currentlyPlaying as CurrentlyPlaying,
          dispatch,
        });
      }
    });

    useEffect(() => {
      if (current) {
        const eventHandlers = buildEventHandlers(current, dispatch);

        Object.entries(eventHandlers).forEach(([event, handler]) => {
          current.addEventListener(event, handler);
        });

        return () => {
          Object.entries(eventHandlers).forEach(([event, handler]) => {
            current.removeEventListener(event, handler);
          });
        };
      }
    });
    return null;
  },
);

export default AudioSourceSetup;
